import type {
  ApplicationsContract,
  CreateCredentialDto,
  ScanEffortLevel,
} from '@terra/shared-contracts-applications';
import type {
  BusinessUnitsContract,
  CreateBusinessUnitDto,
} from '@terra/shared-contracts-business-units';
import { applicationsClient } from '@terra/shared-rest-clients-applications';
import { businessUnitsClient } from '@terra/shared-rest-clients-business-units';
import { tenantAwareApiWrapper, withOrchestratorClient } from './portal-http';

export const portalApplicationSettingsApi = {
  getApplicationScanEffort: (applicationId: string) =>
    tenantAwareApiWrapper<typeof ApplicationsContract.getApplicationScanEffort>(
      {
        endpointFn: (headers) =>
          applicationsClient.getApplicationScanEffort({
            params: { id: applicationId },
            ...withOrchestratorClient(headers),
          }),
      },
    ),

  updateApplicationScanEffort: ({
    applicationId,
    level,
  }: {
    applicationId: string;
    level: ScanEffortLevel;
  }) =>
    tenantAwareApiWrapper<
      typeof ApplicationsContract.updateApplicationScanEffort
    >({
      endpointFn: (headers) =>
        applicationsClient.updateApplicationScanEffort({
          params: { id: applicationId },
          body: { level },
          ...withOrchestratorClient(headers),
        }),
      useBodyMessage: true,
    }),

  getLatestApplicationFullRescan: (applicationId: string) =>
    tenantAwareApiWrapper<
      typeof ApplicationsContract.getLatestApplicationFullRescan
    >({
      endpointFn: (headers) =>
        applicationsClient.getLatestApplicationFullRescan({
          params: { id: applicationId },
          ...withOrchestratorClient(headers),
        }),
    }),

  startApplicationFullRescan: (applicationId: string) =>
    tenantAwareApiWrapper<
      typeof ApplicationsContract.startApplicationFullRescan
    >({
      endpointFn: (headers) =>
        applicationsClient.startApplicationFullRescan({
          params: { id: applicationId },
          ...withOrchestratorClient(headers),
        }),
      useBodyMessage: true,
    }),

  getApplicationFiles: (applicationId: string) =>
    tenantAwareApiWrapper<typeof ApplicationsContract.getApplicationFiles>({
      endpointFn: (headers) =>
        applicationsClient.getApplicationFiles({
          params: { id: applicationId },
          ...withOrchestratorClient(headers),
        }),
    }),

  getAllBusinessUnits: () =>
    tenantAwareApiWrapper<typeof BusinessUnitsContract.getAllBusinessUnits>({
      endpointFn: (headers) =>
        businessUnitsClient.getAllBusinessUnits({
          ...withOrchestratorClient(headers),
        }),
    }),

  createBusinessUnit: (body: CreateBusinessUnitDto) =>
    tenantAwareApiWrapper<typeof BusinessUnitsContract.createBusinessUnit>({
      endpointFn: (headers) =>
        businessUnitsClient.createBusinessUnit({
          body,
          ...withOrchestratorClient(headers),
        }),
      useBodyMessage: true,
    }),

  getApplicationBusinessUnits: (applicationId: string) =>
    tenantAwareApiWrapper<
      typeof BusinessUnitsContract.getApplicationBusinessUnits
    >({
      endpointFn: (headers) =>
        businessUnitsClient.getApplicationBusinessUnits({
          params: { applicationId },
          ...withOrchestratorClient(headers),
        }),
    }),

  updateApplicationBusinessUnits: ({
    applicationId,
    businessUnitIds,
  }: {
    applicationId: string;
    businessUnitIds: string[];
  }) =>
    tenantAwareApiWrapper<
      typeof BusinessUnitsContract.updateApplicationBusinessUnits
    >({
      endpointFn: (headers) =>
        businessUnitsClient.updateApplicationBusinessUnits({
          params: { applicationId },
          body: { businessUnitIds },
          ...withOrchestratorClient(headers),
        }),
      useBodyMessage: true,
    }),

  getCredentials: ({
    applicationId,
    includeDisabled,
  }: {
    applicationId: string;
    includeDisabled: boolean;
  }) =>
    tenantAwareApiWrapper<typeof ApplicationsContract.getCredentials>({
      endpointFn: (headers) =>
        applicationsClient.getCredentials({
          params: { id: applicationId },
          query: { includeDisabled },
          ...withOrchestratorClient(headers),
        }),
    }),

  saveCredential: ({
    applicationId,
    body,
  }: {
    applicationId: string;
    body: CreateCredentialDto;
  }) =>
    tenantAwareApiWrapper<typeof ApplicationsContract.saveCredential>({
      endpointFn: (headers) =>
        applicationsClient.saveCredential({
          params: { id: applicationId },
          body,
          ...withOrchestratorClient(headers),
        }),
      useBodyMessage: true,
    }),

  deleteCredential: ({
    applicationId,
    credentialId,
  }: {
    applicationId: string;
    credentialId: string;
  }) =>
    tenantAwareApiWrapper<typeof ApplicationsContract.deleteCredential>({
      endpointFn: (headers) =>
        applicationsClient.deleteCredential({
          params: { id: applicationId, credentialId },
          ...withOrchestratorClient(headers),
        }),
      useBodyMessage: true,
    }),

  toggleCredentialStatus: ({
    applicationId,
    credentialId,
    isDisabled,
  }: {
    applicationId: string;
    credentialId: string;
    isDisabled: boolean;
  }) =>
    tenantAwareApiWrapper<typeof ApplicationsContract.toggleCredentialStatus>({
      endpointFn: (headers) =>
        applicationsClient.toggleCredentialStatus({
          params: { id: applicationId, credentialId },
          body: { isDisabled },
          ...withOrchestratorClient(headers),
        }),
      useBodyMessage: true,
    }),

  verifySingleCredential: ({
    applicationId,
    credentialId,
    streamingId,
  }: {
    applicationId: string;
    credentialId: string;
    streamingId: string;
  }) =>
    tenantAwareApiWrapper<typeof ApplicationsContract.verifySingleCredential>({
      endpointFn: (headers) =>
        applicationsClient.verifySingleCredential({
          params: { id: applicationId, credentialId },
          body: { streamingId },
          ...withOrchestratorClient(headers),
        }),
    }),

  verifyCredentials: (
    applicationId: string,
    streamingIds?: Record<string, string>,
  ) =>
    tenantAwareApiWrapper<typeof ApplicationsContract.verifyCredentials>({
      endpointFn: (headers) =>
        applicationsClient.verifyCredentials({
          params: { id: applicationId },
          body: streamingIds ? { streamingIds } : undefined,
          ...withOrchestratorClient(headers),
        }),
    }),

  abortCredentialVerification: ({
    applicationId,
    credentialId,
  }: {
    applicationId: string;
    credentialId: string;
  }) =>
    tenantAwareApiWrapper<
      typeof ApplicationsContract.abortCredentialVerification
    >({
      endpointFn: (headers) =>
        applicationsClient.abortCredentialVerification({
          params: { id: applicationId, credentialId },
          ...withOrchestratorClient(headers),
        }),
    }),
};
